import { clearImpersonation, impersonateAs } from '@/auth/impersonation';
import { useSession } from '@/auth/useSession';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { RoleBadgeList } from '@/components/users/role-badge';
import { EmptyArray, emptyQueryOptions, getUsersTypeahead } from '@/lib/api-calls';
import { useDebouncedInput } from '@/lib/common-utils';

import { GetUsersTypeaheadModifier, typeaheadPagesModifier } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { LoaderIcon } from 'lucide-react';
import { ReactNode, useId, useState } from 'react';

export interface ImpersonationSwitcherProps {
  trigger: ReactNode;
}

export const ImpersonationSwitcher = ({ trigger }: ImpersonationSwitcherProps) => {
  const [query, setQuery] = useState<string | undefined>();
  const { loggedInUser, actingUser, isImpersonating } = useSession();
  const [[liveQuery, debouncedQuery], setDebouncedQuery] = useDebouncedInput(query, setQuery);
  const req = {
    ...typeaheadPagesModifier,
    filters: {
      typeahead: debouncedQuery,
    },
  } satisfies GetUsersTypeaheadModifier;
  const { data: response, isPending } = useQuery(isImpersonating ? emptyQueryOptions : getUsersTypeahead(req));
  const inputId = useId();
  const users = (response || EmptyArray).filter((user) => user.id !== loggedInUser.id);

  const impersonationSelection = (
    <>
      <div className="p-2">
        <Label className={'font-bold'} htmlFor={inputId}>
          Impersonate as user:
        </Label>
        <Input
          onChange={(e) => setDebouncedQuery(e.target.value)}
          value={liveQuery}
          id={inputId}
          placeholder="Search user by name or email"
        />
      </div>
      <Separator className="my-2" />
      <div className="py-2">
        {isPending ? (
          <div className="flex justify-center">
            <LoaderIcon />
          </div>
        ) : (
          <div className="">
            {users.map((user) => {
              return (
                <Button
                  variant={'ghost'}
                  className="w-full flex justify-between py-2 mb-2"
                  key={user.id}
                  onClick={() => impersonateAs(user.id)}
                >
                  <div className="space-y-1 flex flex-col items-start">
                    <p className="text-sm font-medium leading-none">{user.fullNameOrEmail}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                  <div className="ml-auto font-medium flex items-center gap-1">
                    <RoleBadgeList userRoles={user.userRoles} />
                  </div>
                </Button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[650px]" collisionPadding={150}>
        <div className="flex flex-col">
          {isImpersonating ? (
            <div>
              <p>
                You are viewing the app as <b>{actingUser.fullNameOrEmail}</b>
              </p>
              <Button type="button" onClick={() => clearImpersonation()}>
                Stop Impersonation
              </Button>
            </div>
          ) : (
            impersonationSelection
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
